import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import NiaApi from '../api/NiaApi'
import SuklUsersApi, { iUserData } from '../api/SuklUsersApi'
import authStorage from '../utils/authStorage'
import { mapSlugToDynamicUserData } from '../utils/mapFormUserData'

type AuthenticationContext = {
  isAuthenticated: boolean,
  loadingAuth: boolean,
  login: () => void,
  logout: () => void,
  userData?: iUserData
  error: string,
}

export const useUserIsAuthenticatedDynamicData = (onAuthenticated: (userDynamicDataMap: any) => void, deps?: any) => {
  const {submissionID} = useParams() as any
  const { isAuthenticated, userData } = useAuthenticationContext()

  useEffect(() => {
    if (isAuthenticated && userData && !submissionID) {
      const userDynamicDataMap = mapSlugToDynamicUserData(userData)
      onAuthenticated(userDynamicDataMap)
    }
  }, [isAuthenticated, userData, submissionID, deps]) // eslint-disable-line react-hooks/exhaustive-deps
}

export const useAuthenticationContext = () => {
  const context = useContext(AuthenticationContext)
  if (!context) {
    throw new Error('useAuthenticationContext must be used within a AuthenticationProvider')
  }
  return context as AuthenticationContext
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export const AuthenticationContext = createContext<AuthenticationContext | undefined>(undefined)

export const AuthenticationProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [loadingAuth, setLoadingAuth] = useState<boolean>(true)

  const [error, setError] = useState('')
  const [userData, setUserData] = useState<iUserData>()

  const loadUserData = async (uuid: string) => {
    let counter = 15

    try {
      while (true) {
        if (counter-- <= 0) {
          throw new Error('Nepodařilo se získat stav přihlášení. Zkuste to prosím znovu později.')
        }

        const status = await NiaApi.getStatus(uuid)

        if (status.stavID === 3) {
          // 3 – platný token, můžeme pokračovat dál
          break
        }
        let message = 'Při ověřování došlo k chybě. Zkuste to prosím znovu. ' + status.stav

        if ([4, 5, 6, 7, 44].includes(status.stavID)) {
          // 4 – Neplatné volání ověření v NIA
          // 5 – Chyba při ověřování v EI
          // 6 – Chyba při ověřování v NIA
          // 7 – Neplatný token (typicky expirovaný / odhlášený uživatel)

          switch (status.stavID) {
            case 4:
              message = 'Neplatné volání ověření v NIA. Zkuste přihlášení zopakovat.'
              break
            case 5:
              message = 'Při ověřování v EI došlo k chybě. Zkuste to prosím znovu později.'
              break
            case 6:
              message = 'Při ověřování v NIA došlo k chybě. Zkuste přihlášení zopakovat.'
              break
            case 7:
              message = 'Platnost přihlášení vypršela nebo je neplatná. Přihlaste se prosím znovu.'
              break
            case 44:
              message = 'Neexistujici osoba v EI.'
              break
          }

          console.error('Proces skončil se statusem', status.stavID, status)

          setError(message)
          setUserData(undefined)
          setIsAuthenticated(false)
          authStorage.clearUuid()
          setLoadingAuth(false)
          return
        }

        // stav 1 a 2 – dočasné stavy, jen čekáme a zkoušíme znovu
        // 1 – Probíhá ověřování v NIA
        // 2 – Probíhá zpracování v EI

        // počkej 1 sekundu a zkus znovu
        await sleep(2000)
      }
    } catch (e) {
      console.log(e)
      setError('Neočekávaná chyba: ' + e.toString())
      setUserData(undefined)
      setIsAuthenticated(false)
      authStorage.clearUuid()
      setLoadingAuth(false)
      return
    }

    // pokud je ulozny token - tak zkontroluj stav a nacti uzivatele
    SuklUsersApi.getUserDetailWithRetry(uuid)
      .then(userDetail => {
        setUserData(userDetail)
        setIsAuthenticated(true)
      })
      .catch(e => {
        //BE vrací 401, pokud k UUID z NIA nedostane uživatele, tj. buď nedokončil přihlášení nebo vypršelo.
        // Pokud to projde, pak se jde na SP službu, tam se taky autentizuje, ale tam jsem dal 403, pokud to neprojde.
        // Což znamená, že služba není pro přihlášeného uživatele NIA ochotna dát data.

        console.error(e)

        const status = e?.response?.status

        if (status === 401) {
          // nedokončené / expirované přihlášení přes NIA
          setError(
            'Nepodařilo se načíst údaje z NIA. Přihlášení nebylo dokončeno nebo vypršela jeho platnost. Zkuste se, prosím, přihlásit znovu.'
          )
        } else if (status === 403) {
          // uživatel NIA nemá oprávnění pro SP službu
          setError(
            'Nemáte oprávnění pro přístup k této službě s aktuálním přihlášením přes NIA.'
          )
        } else if (status === 404) {
          setError(
            'Neplatný identifikátor přihlášení. Zkuste se, prosím, přihlásit znovu.'
          )
        } else {
          // ostatní chyby – technická chyba / neočekávaný stav
          setError(
            'Došlo k technické chybě při načítání údajů uživatele. Zkuste to prosím znovu později.' + e.toString()
          )
        }

        setUserData(undefined)
        setIsAuthenticated(false)
        authStorage.clearUuid()
      }).finally(() => {
      setLoadingAuth(false)
    })
  }

  useEffect(() => {

    const uuid = authStorage.getUuid()

    if (uuid) {
      loadUserData(uuid)
    } else {
      const params = new URLSearchParams(location.search)
      if (params.has('autoLogin')) {
        const newUuid = authStorage.newUuid()
        const target = location.pathname.slice(1) + location.search
        const loginUrl = NiaApi.loginRedirectUrl(newUuid, target)
        window.location.assign(loginUrl)
        return
      }
      setLoadingAuth(false)
    }

  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(() => {
    authStorage.clearUuid()
    setIsAuthenticated(false)
    setUserData(undefined)

    NiaApi.getLogout()
      .catch(e => {
        console.error(e)
      })
    // todo reload ? ne bo neni potreba
  }, [])

  const location = useLocation()
  const currentPath = location.pathname.slice(1) //+ location.search; // cesta + query parametry

  const login = useCallback(() => {
    const uuid = authStorage.newUuid()
    const loginUrl = NiaApi.loginRedirectUrl(uuid, currentPath)
    window.location.assign(loginUrl)
  }, [currentPath])

  const value = useMemo(() => ({
    isAuthenticated,
    loadingAuth,
    logout,
    login,
    userData,
    error,
  }), [isAuthenticated, loadingAuth, logout, login, userData, error])

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  )
}
